import { Tour as TourBase, TourStepProps } from 'antd'
import omit from 'lodash-es/omit'
import without from 'lodash-es/without'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import checkLifecycleIsReady, {
  LifecycleFn,
} from '@/components/common/tour/check-lifecycle-is-ready'
import { WaitForConditionRejectionReason } from '@/utils/wait-for-condition'

// 簡化邏輯，我們不支持上一步的需求，另外想區隔原生的 onFinish 故改名 onComplete
// step 生命週期
// 1. onStart 非同步
// 2. findTarget 非同步
// 3. User 點擊下一步
// 4. onNext 非同步
// 5. onComplete 非同步 (執行完後通常直接執行 newStep 的 onStart)
interface TourStep
  extends Omit<TourStepProps, 'onPrev' | 'onFinish' | 'target'> {
  onStart?: LifecycleFn
  onNext?: LifecycleFn
  onComplete?: LifecycleFn
  target?: TourStepProps['target'] | (() => HTMLElement | null)
}

interface TourProps {
  steps: TourStep[]
  open: boolean
  onClose: (isLastStep: boolean) => void
  onFinish: () => void // 打開最後步驟 modal
  onError: (error: Error | WaitForConditionRejectionReason) => void // 發生非預期的意外執行 AC 所描述的事情
  onOpen?: () => void
}

const Tour: FunctionComponent<TourProps> = ({
  open,
  steps,
  onClose,
  onError,
  onFinish,
  onOpen = (): void => {},
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [newStepIndex, setNewStepIndex] = useState(0)
  const currentStep = useMemo<TourStep>(
    () => steps[currentStepIndex],
    [steps, currentStepIndex],
  )
  const [isReady, setIsReady] = useState(false)
  const cancelFnList = useRef<(() => void)[]>([])

  const resetStatus = (): void => {
    setCurrentStepIndex(0)
    setNewStepIndex(0)
    setIsReady(false)
    cancelFnList.current.forEach((cancelFn) => cancelFn())
    cancelFnList.current = []
  }
  const checkLifecycle = async (fn: LifecycleFn): Promise<void> =>
    await checkLifecycleIsReady(fn, {
      onStart: (cancelFn) => {
        cancelFnList.current = [...cancelFnList.current, cancelFn]
      },
      onResolve: (cancelFn) => {
        cancelFnList.current = without(cancelFnList.current, cancelFn)
      },
    })

  const onErrorRef = useRef(onError)
  useEffect(() => {
    onErrorRef.current = onError
  })

  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  const onFinishRef = useRef(onFinish)
  useEffect(() => {
    onFinishRef.current = onFinish
  })

  const onOpenRef = useRef(onOpen)
  useEffect(() => {
    onOpenRef.current = onOpen
  })

  const handleError = useCallback(
    (error: Error | WaitForConditionRejectionReason) => {
      if ('type' in error && error.type === 'cancel') {
        // cancel 的話不需讓外面知道
        return
      }
      onErrorRef.current(error)
      resetStatus()
    },
    [],
  )
  const handleFinish = useCallback(() => {
    try {
      resetStatus()
      onFinishRef.current()
    } catch (e) {
      handleError(e)
    }
  }, [handleError])
  const handleClose = useCallback(() => {
    try {
      resetStatus()
      onCloseRef.current(currentStepIndex === steps.length - 1)
    } catch (e) {
      handleError(e)
    }
  }, [handleError, currentStepIndex, steps.length])
  const handleChange = useCallback(
    async (newStepIndex: number): Promise<void> => {
      try {
        if (newStepIndex <= currentStepIndex) {
          // 目前不支援上一步
          return
        }

        await checkLifecycle(currentStep.onNext)
        await checkLifecycle(currentStep.onComplete)

        setNewStepIndex(newStepIndex)
      } catch (e) {
        handleError(e)
      }
    },
    [currentStep, currentStepIndex, handleError],
  )

  useEffect(() => {
    if (!open) {
      return
    }
    onOpenRef.current()
  }, [open])

  useEffect(() => {
    resetStatus()
  }, [steps, open])

  useEffect(() => {
    if (!open) {
      return
    }

    const isInit = currentStepIndex === 0 && newStepIndex === 0
    if (!isInit && newStepIndex === currentStepIndex) {
      return
    }

    const effectFn = async (): Promise<void> => {
      const newStep = steps[newStepIndex]
      if (!newStep) {
        return
      }

      try {
        await checkLifecycle(newStep?.onStart)
        await checkLifecycle(
          () => () =>
            !newStep.target ||
            newStep.target instanceof HTMLElement ||
            !!newStep.target(),
        )

        setCurrentStepIndex(newStepIndex)

        if (!isReady) {
          setIsReady(true)
        }
      } catch (e) {
        handleError(e)
      }
    }

    effectFn()
  }, [currentStepIndex, handleError, isReady, newStepIndex, open, steps])

  // 如果 Antd.TourStep 有 onNext 事件的話，則 Antd.Tour 的 onChange 不會執行，故先把它拔掉，統一在 handleChange
  const _steps = useMemo(() => {
    return steps.map((step) => {
      return {
        ...omit(step, ['onNext']),
        prevButtonProps: { style: { display: 'none' } },
      }
    }) as TourStepProps[]
  }, [steps])

  return (
    <>
      <TourBase
        current={currentStepIndex}
        open={open && isReady}
        steps={_steps}
        onChange={handleChange}
        onClose={handleClose}
        onFinish={handleFinish}
      />
      <FullScreenMask
        isOnStepTransition={currentStepIndex !== newStepIndex}
        isOnTourFlow={open && isReady}
      />
      <TourStyle hiddenTour={currentStepIndex !== newStepIndex} />
    </>
  )
}

export type { TourStep, TourProps }
export default Tour

// 在轉換下一步中間隱藏原有的 tour, bug ticket ref: https://www.notion.so/ikala/Staging-f9da852c9ce347fdba805425552a9dc8?pvs=4
const TourStyle = createGlobalStyle<{ hiddenTour?: boolean }>`
  //需大於 dropdown menu 的 index (1050)
  .ant-tour-mask {
    z-index: 1051 !important;
    display: ${(props): string => (props.hiddenTour ? 'none' : 'block')};
  }
  .ant-tour {
    z-index: 1052 !important;
    display: ${(props): string => (props.hiddenTour ? 'none' : 'block')};
  }
  
  //避免被 close icon 蓋住
  .ant-tour-header {
        padding: 16px 38px 16px 16px !important;
  }
`

const FullScreenMask = styled.div<{
  isOnStepTransition?: boolean
  isOnTourFlow: boolean
}>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(props): string =>
    props.isOnStepTransition ? 'rgba(0, 0, 0, 0.5)' : 'transparent'};
  z-index: 1052;
  display: ${(props): string => (props.isOnTourFlow ? 'block' : 'none')};
`
